import {
	type MutationFunctionContext,
	skipToken,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import { isHttpError, isNetworkError } from "@/shared/api/errors"
import { QUERY_CONFIG } from "@/shared/config/query"
import { assertSessionVersion } from "@/shared/lib/auth"
import { applyVoiceMessage } from "../model/apply-voice-message"
import { voiceKeys } from "../model/keys"
import type { VoiceMessage } from "../model/types"
import {
	deleteVoiceMessage,
	finishVoicePlayback,
	getVoiceMessage,
	getVoiceMessageUrl,
	holdVoiceMessage,
	publishVoiceMessage,
	readConflictVoiceMessage,
	releaseVoiceMessage,
} from "./client"

const RETRY_BASE_MS = 1_000

interface SessionBound {
	sessionVersion: number
}

interface FriendBound {
	friendId: number
}

async function runInSession<TData>(
	variables: SessionBound,
	run: () => Promise<TData>,
): Promise<TData> {
	assertSessionVersion(variables.sessionVersion)
	try {
		return await run()
	} finally {
		assertSessionVersion(variables.sessionVersion)
	}
}

function retryVoiceCommand(failureCount: number, error: Error): boolean {
	if (failureCount >= QUERY_CONFIG.MUTATION_NETWORK_RETRY) return false
	if (isNetworkError(error)) return true
	return isHttpError(error) && error.status >= 500
}

function retryVoicePublish(failureCount: number, error: Error): boolean {
	return failureCount < 1 && retryVoiceCommand(failureCount, error)
}

function retryVoiceCommandDelay(failureCount: number): number {
	return RETRY_BASE_MS * 2 ** failureCount
}

function applyVoiceResult(
	voiceMessage: VoiceMessage,
	variables: FriendBound,
	_onMutateResult: unknown,
	context: MutationFunctionContext,
): void {
	applyVoiceMessage(context.client, variables.friendId, voiceMessage)
}

function applyVoiceConflict(
	error: Error,
	variables: FriendBound,
	_onMutateResult: unknown,
	context: MutationFunctionContext,
): void {
	const voiceMessage = readConflictVoiceMessage(error)
	if (voiceMessage) {
		applyVoiceMessage(context.client, variables.friendId, voiceMessage)
	}
}

export function useVoiceMessage(friendId: number) {
	const queryClient = useQueryClient()
	return useQuery({
		queryKey: voiceKeys.message(friendId),
		queryFn: async (): Promise<VoiceMessage> => {
			const { message, url } = await getVoiceMessage(friendId)
			if (url !== null && message.phase === "ready") {
				queryClient.setQueryData(voiceKeys.url(message.message_id), url)
			}
			return message
		},
	})
}

function voiceUrlQueryFn(messageId: string | null) {
	if (messageId === null) return skipToken
	return () => getVoiceMessageUrl(messageId)
}

export function useVoiceUrl(messageId: string | null) {
	return useQuery({
		queryKey: voiceKeys.url(messageId),
		queryFn: voiceUrlQueryFn(messageId),
	})
}

export function useHoldVoiceMessage() {
	return useMutation({
		mutationFn: (variables: SessionBound & { friendId: number }) =>
			runInSession(variables, () =>
				holdVoiceMessage({ friendId: variables.friendId }),
			),
		onSuccess: (data, variables, _onMutateResult, context) =>
			applyVoiceMessage(context.client, variables.friendId, data.message),
		onError: applyVoiceConflict,
		retry: retryVoiceCommand,
		retryDelay: retryVoiceCommandDelay,
	})
}

export function useReleaseVoiceMessage() {
	return useMutation({
		mutationFn: (
			variables: SessionBound & { friendId: number; holdId: string },
		) =>
			runInSession(variables, () =>
				releaseVoiceMessage({ holdId: variables.holdId }),
			),
		onSuccess: applyVoiceResult,
		onError: applyVoiceConflict,
		retry: retryVoiceCommand,
		retryDelay: retryVoiceCommandDelay,
	})
}

export function usePublishVoiceMessage() {
	return useMutation({
		mutationFn: (
			variables: SessionBound & {
				friendId: number
				holdId: string
				clientMessageId: string
				file: Blob
				fileName: string
				onAttemptError: (error: unknown) => void
			},
		) =>
			runInSession(variables, async () => {
				try {
					return await publishVoiceMessage(variables)
				} catch (error) {
					variables.onAttemptError(error)
					throw error
				}
			}),
		onSuccess: applyVoiceResult,
		onError: applyVoiceConflict,
		retry: retryVoicePublish,
		retryDelay: retryVoiceCommandDelay,
	})
}

export function useDeleteVoiceMessage() {
	return useMutation({
		mutationFn: (
			variables: SessionBound & { friendId: number; messageId: string },
		) =>
			runInSession(variables, () =>
				deleteVoiceMessage({
					friendId: variables.friendId,
					messageId: variables.messageId,
				}),
			),
		onSuccess: applyVoiceResult,
		onError: applyVoiceConflict,
		retry: retryVoiceCommand,
		retryDelay: retryVoiceCommandDelay,
	})
}

export function useFinishVoicePlayback() {
	return useMutation({
		mutationFn: (
			variables: SessionBound & { friendId: number; messageId: string },
		) =>
			runInSession(variables, () =>
				finishVoicePlayback({
					friendId: variables.friendId,
					messageId: variables.messageId,
				}),
			),
		onSuccess: applyVoiceResult,
		onError: applyVoiceConflict,
		retry: retryVoiceCommand,
		retryDelay: retryVoiceCommandDelay,
	})
}
